import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Menu, MenuItem } from '../../ui/Menu.js';
import { Plus } from 'lucide-react';
import { prepare } from '../../platform/prepare.js';
import { type ProjectTool } from '@werft/domain';
import type { ProjectSnapshotDto } from '@werft/contracts';
import { t, type MessageKey } from '../../i18n/index.js';
import { useTalkState } from '../../platform/queries.js';

/**
 * Projekt-Insel (Paket P9): Werkzeug-Leiste, Werkzeuge zuschalten und die Projektübersicht.
 *
 * Klein anfangen: Ein neues Projekt zeigt nur Aufgaben und die Ansichtsauswahl, keine zusätzlichen Reiter. Die Projektleitung schaltet die
 * Übersicht im Projektmenü („Werkzeuge …“) zu; erst dann erscheint die Leiste „Übersicht · Aufgaben · +“.
 * Ausblenden entfernt keine Daten (Angeheftetes bleibt angeheftet).
 */

export type Tool = 'tasks' | ProjectTool;

export const toolLabel = (x: Tool) => t(`tools.${x}` as MessageKey);

/**
 * Feste Werkzeugleiste – zusätzliche Reiter erst, wenn Werkzeuge zugeschaltet sind. Rechts daneben (`end`) die
 * Aktionen des Werkzeugs, z. B. die Ansichtsauswahl der Aufgaben – nicht im Projektkopf (Audit F03/F09: der Kopf bleibt
 * in allen Werkzeugen gleich und lässt dem Projektnamen Platz). Am Handy scrollt die Leiste in sich, nie die Seite.
 */
export function ToolNav({ snapshot, current, onManage, end }: { snapshot: ProjectSnapshotDto; current: Tool; onManage: () => void; end?: ReactNode }) {
  const { project } = snapshot;
  const nav = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const strip = nav.current, active = strip?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!strip || !active) return;
    if (active.offsetLeft < strip.scrollLeft) strip.scrollLeft = active.offsetLeft;
    else if (active.offsetLeft + active.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = active.offsetLeft + active.offsetWidth - strip.clientWidth;
  }, [current, project.id]);
  // G01: läuft der Streifen über, zeigt ein Verlauf am Rand, dass er scrollt (die Scrollleiste ist ausgeblendet)
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const strip = nav.current;
    if (!strip || typeof ResizeObserver === 'undefined') return;
    const check = () => setOverflows(strip.scrollWidth > strip.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(strip);
    return () => ro.disconnect();
  }, [project.order.length]);
  // Gespräche: Ungelesenes am Reiter (nur wenn zugeschaltet)
  const talk = useTalkState(project.id, project.tools.includes('talk'));
  const unread = (talk.data?.chatUnread ?? 0) + (talk.data?.topicsUnread ?? 0);

  // W07: gemeinsame Reihenfolge der Projektleitung (Startseite wird nicht automatisch nach vorn gezogen)
  const order: Tool[] = project.order;
  const to = { tasks: '/p/$projectId', overview: '/p/$projectId/uebersicht', files: '/p/$projectId/dateien', docs: '/p/$projectId/notizen', talk: '/p/$projectId/gespraeche', intake: '/p/$projectId/eingang', approvals: '/p/$projectId/freigaben', events: '/p/$projectId/termine', people: '/p/$projectId/personen', budget: '/p/$projectId/budget', resources: '/p/$projectId/einsaetze', goals: '/p/$projectId/ziele', time: '/p/$projectId/zeit' } as const;

  // ADR 0103 (Entwicklerbericht „Werkzeugleiste“): bei vielen Werkzeugen die ersten in der Leiste, der Rest unter
  // „Weitere“; das aktuelle Werkzeug bleibt immer sichtbar. Reihenfolge = die der Projektleitung.
  const navigate = useNavigate();
  const many = order.length > MAX_TOOLS;
  const primary = many ? [...order.slice(0, MAX_TOOLS - 1), ...(order.indexOf(current) >= MAX_TOOLS - 1 ? [current] : [])] : order;
  const rest = many ? order.filter((x) => !primary.includes(x)) : [];
  const item = (x: Tool) => (
    <Link
      key={x}
      to={to[x]}
      params={{ projectId: project.id }}
      activeOptions={{ exact: true, includeSearch: false }}
      aria-current={current === x ? 'page' : undefined}
      className={tabClass}
      // 0.158 (ADR 0164): Absicht → Zielmodul und Erstabfrage vorbereiten (Zeigen, Tastatur, Touch; Hover ist keine Voraussetzung)
      onPointerEnter={() => current !== x && prepare(x, project.id)}
      onFocus={() => current !== x && prepare(x, project.id)}
      onTouchStart={() => current !== x && prepare(x, project.id)}
    >
      {toolLabel(x)}
      {x === 'talk' && unread > 0 && (
        <span className="ml-2 rounded-full bg-[var(--color-accent)] px-2 text-xs font-medium text-on-accent" aria-label={t('talk.unread', { n: unread })}>
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
  return (
    // Audit 0.36 F04: am Handy stehen alle Werkzeuge sichtbar (bei Bedarf in zwei Zeilen) und die Aufgabenansicht in
    // einer eigenen Zeile darunter – beide konkurrieren nicht mehr um denselben Platz. Ab sm wie bisher eine Zeile.
    <div className="project-tools mb-6 flex min-h-14 flex-wrap items-center gap-x-2 gap-y-1 border-b border-zinc-200 pb-2 sm:flex-nowrap dark:border-zinc-700">
      <nav aria-label={t('tools.nav')} className="flex min-w-0 basis-full flex-wrap items-center gap-x-1 sm:basis-auto sm:flex-1 sm:flex-nowrap">
      <div ref={nav} data-tools-overflow={overflows ? '' : undefined} className={`relative flex min-w-0 basis-full flex-wrap items-center gap-x-1 sm:basis-auto sm:flex-1 sm:flex-nowrap sm:overflow-x-auto sm:[scrollbar-width:none] ${overflows ? 'sm:[mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)]' : ''}`}>
        {project.tools.length ? primary.map(item) : <span className="px-3 text-sm font-semibold">{toolLabel('tasks')}</span>}
      </div>
      {/* G01 (0.143): „Weitere“ und „Werkzeuge verwalten“ sind feste Endaktionen – nie im Scrollstreifen abgeschnitten */}
      {(rest.length > 0 || (project.role === 'lead' && project.tools.length > 0)) && (
        <div className="flex shrink-0 items-center gap-x-1" data-tools-end="">
          {rest.length > 0 && (
            <span data-tools-more="" className="shrink-0 text-sm">
              <Menu label={t('tools.overflowLabel', { n: rest.length })} text trigger={<>{t('tools.overflow', { n: rest.length })}{rest.includes('talk') && unread > 0 && <span className="ml-2 size-2 rounded-full bg-[var(--color-accent)]" aria-hidden />}</>}>
                {rest.map((x) => (
                  <MenuItem key={x} onSelect={() => void navigate({ to: to[x], params: { projectId: project.id } })}>
                    {toolLabel(x)}
                    {x === 'talk' && unread > 0 && ` (${unread > 99 ? '99+' : unread})`}
                  </MenuItem>
                ))}
              </Menu>
            </span>
          )}
          {project.role === 'lead' && project.tools.length > 0 && (
            <button type="button" onClick={onManage} aria-label={t('tools.manage')} title={t('tools.manage')} className="grid size-11 shrink-0 place-items-center rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800">
              <Plus className="size-4" aria-hidden />
            </button>
          )}
        </div>
      )}
      </nav>
      {/* Feste zweite Gruppe; am Handy wird die Ansicht zur kompakten Auswahl. */}
      {end && <div className="flex min-h-11 shrink-0 items-center justify-start max-sm:has-[>[aria-hidden=true]]:hidden sm:justify-end">{end}</div>}
    </div>
  );
}

/** ab hier „Weitere“ (Aufgaben + sechs Werkzeuge passen in eine Zeile) */
const MAX_TOOLS = 7;

const tabClass =
  'min-h-11 shrink-0 whitespace-nowrap px-3 sm:px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900 aria-[current=page]:font-medium aria-[current=page]:text-zinc-900 aria-[current=page]:shadow-[inset_0_-2px_0_var(--color-accent)] dark:text-zinc-400 dark:hover:text-zinc-100 dark:aria-[current=page]:text-zinc-100';

